/**
 * The shared optimistic-mutation helper.
 *
 * Two of this app's three drag surfaces — the Backlog and the Board — do the
 * same four things on a drop: apply the change to the local cache before the
 * network round-trip, recompute whatever depends on order or load through
 * @rundown/domain so the screen never shows a value the server itself would not
 * compute, roll every bit of that back if the write fails, and never let the
 * realtime echo of the write we just made flicker the screen a second time.
 * Writing it once here is what keeps a Board built later from quietly
 * reimplementing (and subtly diverging from) whatever the Backlog already got
 * right.
 *
 * ── The third surface, and why it is not on this ────────────────────────────
 * This header used to name Planning as well. It never used it, which read as
 * drift — but converting it would have been the mistake.
 *
 * Backlog and Board both mutate a LIST OF ROWS they already hold, and the
 * server hands back the row that changed, so `reconcile` is a `setQueryData`
 * that swaps one element. Planning's screen is assembled from three queries
 * (projection, backlog, delivery sprints) and what a drop changes is a
 * PROJECTION — a derived view whose recomputation needs sprints, capacity and
 * every other backlog item. The mutation returns a request; nothing in the
 * response is shaped like anything in those caches.
 *
 * So Planning marks the card pending, lets it render in the column it was
 * dropped on, and refetches. That gives the same immediate feedback by a
 * different route, and the one cost — the brief repaint this file's
 * "reconcile, not invalidate" note warns about — is the honest price of not
 * rebuilding `project()` in a cache updater.
 *
 * The rule this file is really for still holds: do not write a FOURTH
 * lifecycle. Use this one where the shape fits, and Planning's where it does
 * not.
 *
 * ── Rank order, not domain data, is what this file computes ────────────────
 * `reorderByRank` calls `rankBetween` from @rundown/domain — the same function
 * the API calls inside the transaction that commits a drag — so the position a
 * card jumps to on drop is the position it will still be in once the server
 * responds. It deliberately does NOT call `project()`: that function needs
 * sprints, capacity and every other backlog item to mean anything, which is
 * context only the Backlog and Planning features have. Recomputing a
 * projection for instant feedback is therefore each feature's own job, done by
 * feeding its locally-reordered list back through `project()` inside its own
 * `applyOptimistically` — this file supplies the mutation lifecycle that call
 * happens inside, not the call itself.
 *
 * ── Why reconcile, not invalidate ───────────────────────────────────────────
 * This is the whole of the flicker defence, not half of it. useRealtime.ts used
 * to claim the other half — suppressing the echo of this tab's own write — and
 * never did; see the note there for why that guard was removed rather than
 * finished. So the *response* path is what matters: `onSuccess` calls the caller's
 * `reconcile`, which is expected to merge the server's authoritative row into
 * the cache with `setQueryData`, never to `invalidateQueries` the same key.
 * Calling invalidate there would refetch, and the refetch would land between
 * "optimistic value showing" and "realtime echo suppressed" — repainting the
 * row back to its pre-drag position for one frame and forward again. Two
 * separate mechanisms, two separate halves of the same flicker.
 */

import { useMutation, type QueryClient, type QueryKey, type UseMutationResult } from '@tanstack/react-query';
import { rankBetween } from '@rundown/domain';

/** What `onMutate` hands `onError`: exactly enough to put every touched cache
 * back to what `getQueryData` saw before the optimistic write. Snapshotting
 * the real cache value (rather than re-deriving the "previous" state by hand)
 * is what keeps the rollback correct even if something else — another
 * mutation, a realtime invalidation — touched the same cache in between. */
interface Snapshot {
  readonly queryKey: QueryKey;
  readonly data: unknown;
}

export interface OptimisticMutationConfig<TVariables, TResult> {
  readonly queryClient: QueryClient;
  readonly mutationFn: (variables: TVariables) => Promise<TResult>;
  /** Every cache key this mutation reads or writes. Used to cancel in-flight
   * fetches before the optimistic write (a fetch that resolves after our write
   * but was started before it would otherwise clobber it with stale data) and
   * to snapshot for rollback. List every key `applyOptimistically` touches. */
  readonly affectedQueryKeys: readonly QueryKey[];
  /** Write the change into the cache(s), synchronously, before the network
   * call resolves. Read the current data with `queryClient.getQueryData` and
   * write the new value with `queryClient.setQueryData` — do not hold your own
   * copy of "the list", because by the time this runs it may already differ
   * from what the component last rendered. */
  readonly applyOptimistically: (queryClient: QueryClient, variables: TVariables) => void;
  /** Merge the server's answer in once it arrives. Prefer `setQueryData` over
   * `invalidateQueries` — see the file header for why. */
  readonly reconcile: (queryClient: QueryClient, result: TResult, variables: TVariables) => void;
}

/**
 * `useMutation` with the optimistic-apply / rollback / reconcile lifecycle
 * wired up generically. A caller supplies *what* to change; this supplies
 * *when* — cancel, snapshot, apply, and (on failure) restore exactly what was
 * snapshotted.
 */
export function useOptimisticMutation<TVariables, TResult>(
  config: OptimisticMutationConfig<TVariables, TResult>,
): UseMutationResult<TResult, Error, TVariables, readonly Snapshot[]> {
  const { queryClient, mutationFn, affectedQueryKeys, applyOptimistically, reconcile } = config;

  return useMutation({
    mutationFn,
    onMutate: async (variables) => {
      await Promise.all(affectedQueryKeys.map((queryKey) => queryClient.cancelQueries({ queryKey })));

      const snapshots: Snapshot[] = affectedQueryKeys.map((queryKey) => ({
        queryKey,
        data: queryClient.getQueryData(queryKey),
      }));

      applyOptimistically(queryClient, variables);

      return snapshots;
    },
    onError: (_error, _variables, snapshots) => {
      for (const snapshot of snapshots ?? []) {
        queryClient.setQueryData(snapshot.queryKey, snapshot.data);
      }
    },
    onSuccess: (result, variables) => {
      reconcile(queryClient, result, variables);
    },
  });
}

/** The neighbours a drop landed between, named by RANK order (before's rank is
 * lower, after's is higher) rather than by screen position — identical to the
 * API's RankBacklogInput, on purpose: whatever computed these two ids from a
 * drag gesture can hand them straight to the network call with no translation. */
export interface RankNeighbours {
  readonly beforeId?: string | null;
  readonly afterId?: string | null;
}

/**
 * Reorder a rank-ordered list for instant feedback, using the identical
 * fractional-key algorithm (`rankBetween`) the server runs inside its write
 * transaction. `items` must already be sorted highest-rank-first — the
 * direction every rank-ordered list in this app (the backlog, the board) is
 * fetched and rendered in, per requests.service.ts's `sort: 'backlog'`.
 *
 * Taking `beforeId`/`afterId` rather than a target index sidesteps a whole
 * class of off-by-one bugs an index would invite (is it the index in the
 * array with the moved item still in it, or without? before or after
 * dnd-kit's own `arrayMove`?) — the caller already has to compute these two
 * ids to make the PATCH call, so this reuses exactly that, and the reinsertion
 * point falls straight out of `afterId`'s position rather than being passed
 * in and potentially disagreeing with it.
 */
export function reorderByRank<T>(
  items: readonly T[],
  movedId: string,
  neighbours: RankNeighbours,
  idOf: (item: T) => string,
  rankOf: (item: T) => string,
  withRank: (item: T, rank: string) => T,
): readonly T[] {
  const moved = items.find((item) => idOf(item) === movedId);
  if (!moved) return items;

  const beforeItem = neighbours.beforeId
    ? items.find((item) => idOf(item) === neighbours.beforeId)
    : undefined;
  const afterItem = neighbours.afterId ? items.find((item) => idOf(item) === neighbours.afterId) : undefined;
  const newRank = rankBetween(beforeItem ? rankOf(beforeItem) : null, afterItem ? rankOf(afterItem) : null);

  const without = items.filter((item) => idOf(item) !== movedId);
  // Reinsert directly after `afterId` (rank must sort below it) — or at the
  // front when there is no upper neighbour, i.e. the drop was to the very top.
  const insertAt = afterItem ? without.findIndex((item) => idOf(item) === neighbours.afterId) + 1 : 0;

  const result = [...without];
  result.splice(insertAt, 0, withRank(moved, newRank));
  return result;
}
